import FloatingShape from "./FloatingShape";
import { Link } from "react-router";
import { useAuthStore } from "../store/authStore";

const Home = () => {
  const { isAdmin } = useAuthStore();
  const showAdmin = isAdmin();

  const features = [
    {
      name: "Browse and search public fields",
      guest: true,
      user: true,
      member: true,
      admin: true,
    },
    {
      name: "Filter fields by tags and versions",
      guest: false,
      user: false,
      member: true,
      admin: true,
    },    {
      name: "Browse and search eligible internal fields",
      guest: false,
      user: false,
      member: true,
      admin: true,
    },
    {
      name: "See notice for fields under review",
      guest: true,
      user: true,
      member: true,
      admin: true,
    },
    {
      name: "See changes for fields under review",
      guest: false,
      user: false,
      member: true,
      admin: true,
    },
    {
      name: "Browse and search for upcoming fields",
      guest: false,
      user: false,
      member: true,
      admin: true,
    },
    {
      name: "Export fields (CSV, JSON)",
      guest: false,
      user: true,
      member: true,
      admin: true,
    },
    {
      name: "Export fields (PDF, XLSX)",
      guest: false,
      user: false,
      member: true,
      admin: true,
    },
  ];

  const adminFeatures = [
    {
      name: "Edit fields",
      guest: false,
      user: false,
      member: false,
      admin: true,
    },
    {
      name: "Import fields",
      guest: false,
      user: false,
      member: false,
      admin: true,
    },
    {
      name: "Manage tags",
      guest: false,
      user: false,
      member: false,
      admin: true,
    },
    {
      name: "Manage user roles",
      guest: false,
      user: false,
      member: false,
      admin: true,
    },
    {
      name: "Manage references to other data standards",
      guest: false,
      user: false,
      member: false,
      admin: true,
    },
    {
      name: "Set current version",
      guest: false,
      user: false,
      member: false,
      admin: true,
    },
  ];

  return (
    <div className="min-h-[800px] h-full border border-t-0 bg-gradient-to-br from-cyan-900 via-cyan-800 to-cyan-900 flex flex-col relative overflow-hidden">
      <FloatingShape
        color="bg-cyan-500"
        size="w-64 h-64"
        top="0%"
        left="20%"
        delay={0}
      />
      <FloatingShape
        color="bg-cyan-500"
        size="w-48 h-48"
        top="70%"
        left="80%"
        delay={5}
      />
      <FloatingShape
        color="bg-cyan-500"
        size="w-32 h-32"
        top="40%"
        left="10%"
        delay={2}
      />

      <div className="ml-10 mr-10 text-gray-100 z-10">
        <h1 className="text-3xl text-white font-bold mt-20 mb-16">
          Have a look into the openfunds field definitions by choosing{" "}
          <Link
            to="/list"
            className="text-gray-100 hover:text-cyan-900 font-bold bg-opacity-0 hover:bg-gray-100 rounded-lg transition duration-300"
          >
            &quot;Library&quot;
          </Link>{" "}
        </h1>

        <p className="text-lg leading-relaxed mb-6">
          Within &quot;Library&quot;, you can explore the openfunds fields shown
          in a table of all fields. For each field you can view more details by
          clicking the &quot;Show&quot; button. In the detail view you are also
          notified if a field is currently under review.
        </p>

        <p className="text-lg leading-relaxed mb-6">
          Sign up for a free account and get access to downloading all public
          openfunds field definitions in a CSV or JSON format.
        </p>

        <p className="text-lg leading-relaxed mb-6">
          If your company supports openfunds as an active member you can apply
          for additional features. In this case, please contact the{" "}
          <a
            href="https://openfunds.org/about/contact/"
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:text-cyan-300 transition duration-200"
          >
            openfunds team
          </a>{" "}
          and we will provide you with the necessary user rights.
        </p>

        <p className="text-lg leading-relaxed mb-8">
          The table below compares the features available to different types of
          users. Both, guests (no login) and users (login necessary) are free of
          charge. Employees of an openfunds active member have access to even
          more features.
        </p>

        {/* Feature comparison table */}
        <div className={showAdmin ? "max-w-3xl mb-10" : "max-w-2xl mb-10"}>
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-gray-500">
                <th
                  className={`${showAdmin ? "py-1.5" : "py-3"} pr-4 font-semibold`}
                >
                  Feature
                </th>
                <th
                  className={`${showAdmin ? "py-1.5" : "py-3"} px-4 text-center font-semibold`}
                >
                  Guest
                </th>
                <th
                  className={`${showAdmin ? "py-1.5" : "py-3"} px-4 text-center font-semibold`}
                >
                  User
                </th>
                <th
                  className={`${showAdmin ? "py-1.5" : "py-3"} ${showAdmin ? "px-4" : "pl-4"} text-center font-semibold`}
                >
                  Member
                </th>
                {showAdmin && (
                  <th className="py-1.5 pl-4 text-center font-semibold">
                    Admin
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {features.map((feature, index) => (
                <tr
                  key={index}
                  className={`border-b border-gray-600/50 ${!showAdmin && index === features.length - 1 ? "last:border-b-0" : ""}`}
                >
                  <td className={`${showAdmin ? "py-1.5" : "py-3"} pr-4`}>
                    {feature.name}
                  </td>
                  <td
                    className={`${showAdmin ? "py-1.5" : "py-3"} px-4 text-center`}
                  >
                    {feature.guest ? (
                      <span className="text-green-400">✓</span>
                    ) : (
                      <span className="text-red-400">✗</span>
                    )}
                  </td>
                  <td
                    className={`${showAdmin ? "py-1.5" : "py-3"} px-4 text-center`}
                  >
                    {feature.user ? (
                      <span className="text-green-400">✓</span>
                    ) : (
                      <span className="text-red-400">✗</span>
                    )}
                  </td>
                  <td
                    className={`${showAdmin ? "py-1.5" : "py-3"} ${showAdmin ? "px-4" : "pl-4"} text-center`}
                  >
                    {feature.member ? (
                      <span className="text-green-400">✓</span>
                    ) : (
                      <span className="text-red-400">✗</span>
                    )}
                  </td>
                  {showAdmin && (
                    <td className="py-1.5 pl-4 text-center">
                      {feature.admin ? (
                        <span className="text-green-400">✓</span>
                      ) : (
                        <span className="text-red-400">✗</span>
                      )}
                    </td>
                  )}
                </tr>
              ))}
              {showAdmin &&
                adminFeatures.map((feature, index) => (
                  <tr
                    key={`admin-${index}`}
                    className={`border-b border-gray-600/50 ${index === adminFeatures.length - 1 ? "border-b-0" : ""}`}
                  >
                    <td className="py-1.5 pr-4">{feature.name}</td>
                    <td className="py-1.5 px-4 text-center">
                      <span className="text-red-400">✗</span>
                    </td>
                    <td className="py-1.5 px-4 text-center">
                      <span className="text-red-400">✗</span>
                    </td>
                    <td className="py-1.5 px-4 text-center">
                      <span className="text-red-400">✗</span>
                    </td>
                    <td className="py-1.5 pl-4 text-center">
                      <span className="text-green-400">✓</span>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Home;
